import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal,
  contentChild,
  AfterContentInit
} from '@angular/core';

import { IdService } from '../../services/id.service';
import { SelectionChangeEvent } from '../../domain/selectable.domain';
import { CardPrimaryActionComponent } from './card-primary-action.component';

type CardType = 'elevated' | 'outlined' | 'filled' | 'filled-tint' | 'gradient-highlight';

@Component({
  selector: 'td-wb-card',
  template: '<ng-content />',
  styleUrls: ['card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.td-wb-card]': 'true',
    '[class.td-wb-card--clickable]': 'primaryAction() !== undefined',
    '[class.td-wb-card--selected]': 'selectable() && selected()',
    '[class.td-wb-card--flex]': 'horizontal()',
    '[class.td-wb-card--compact]': 'compact()',
    '[class.td-wb-card--elevated]': 'type() === "elevated"',
    '[class.td-wb-card--outlined]': 'type() === "outlined"',
    '[class.td-wb-card--filled]': 'type() === "filled"',
    '[class.td-wb-card--filled-tint]': 'type() === "filled-tint"',
    '[class.td-wb-card--gradient-highlight]': 'type() === "gradient-highlight"'
  }
})
export class CardComponent implements OnInit, AfterContentInit {
  private static readonly ID_PREFIX: string = 'td-wb-card';

  private readonly idService: IdService = inject(IdService);

  readonly id: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
  readonly horizontal: InputSignal<boolean> = input(false);
  readonly compact: InputSignal<boolean> = input(false);
  readonly type: InputSignal<CardType> = input<CardType>('elevated');
  readonly item: InputSignal<unknown | undefined> = input<unknown | undefined>(undefined);
  readonly selectable: WritableSignal<boolean> = signal(false);
  readonly primaryAction: Signal<CardPrimaryActionComponent | undefined> = contentChild(CardPrimaryActionComponent);
  readonly action: OutputEmitterRef<SelectionChangeEvent<CardComponent>>
    = output<SelectionChangeEvent<CardComponent>>();

  readonly selected: WritableSignal<boolean> = signal(false);

  ngOnInit(): void {
    if (this.id() === undefined) {
      this.id.set(this.idService.generateNamespaceSequentialId(CardComponent.ID_PREFIX));
    }
  }

  ngAfterContentInit(): void {
    this.primaryAction()?.action.subscribe((event: SelectionChangeEvent<CardPrimaryActionComponent>) => {
      this.selected.set(!this.selected());
      this.action.emit(new SelectionChangeEvent<CardComponent>(this, event.fromView));
    });
  }

  setSelected(value: boolean): void {
    this.selected.set(value);
  }
}
